
import {
  defineComponent,
  reactive,
  toRefs,
  ref,
  computed,
  onMounted,
  onUnmounted,
  onBeforeUnmount,
  nextTick
} from 'vue'
import { useRoute } from 'vue-router'
import * as echarts from 'echarts'
import sentimentApi from '@/api/sentimentBarometer'
import ws from '@/websocket/index'
import { MESSAGEID_DEF } from '@/websocket/quotationWebsocket'

interface SectorRow {
  key: string
  name: string
  symbol: string
  market: string
  color: string
  series: [number, number][]  // [timestamp, yi]
  latestYi: string
}

const MARKET_ID_MAP: Record<string, number> = {
  ahzbsector: 12,
  sz: 0,
  sh: 1,
  bj: 3,
}

// 五段颜色：红、橙、黄、绿、蓝
const RANK_COLORS = ['#e84040', '#ff7043', '#ffa726', '#66bb6a', '#42a5f5']

// 颜色刷新时间点（分钟数）
const COLOR_SLOTS = [
  { min: 570, key: '930' },   // 9:30
  { min: 690, key: '1130' },  // 11:30
  { min: 900, key: '1500' },  // 15:00
]

// C++ 调用：交易日切换时刷新页面
function on_tradingday_changed() {
  console.log('C++ 调用：交易日切换时刷新页面')
  window.location.reload()
}

// 按最新累计净流入排名比较（desc=true 为高到低，false 为低到高）
function compareSectorByLatestYi(a: SectorRow, b: SectorRow, desc = true) {
  const av = parseFloat(a.latestYi) || 0
  const bv = parseFloat(b.latestYi) || 0
  return desc ? bv - av : av - bv
}

function parseLatestYi(row: SectorRow): number | null {
  if (row.latestYi === '--') return null
  const v = parseFloat(row.latestYi)
  return isNaN(v) ? null : v
}

// 取 latestYi 最大的板块
function pickTopSectorByLatestYi(list: SectorRow[]): SectorRow | null {
  let top: SectorRow | null = null
  let maxYi = -Infinity
  for (const s of list) {
    const yi = parseLatestYi(s)
    if (yi == null) continue
    if (yi > maxYi) {
      maxYi = yi
      top = s
    }
  }
  return top
}

// 根据最新累计净流入值排名分配颜色
function assignRankColors(list: SectorRow[]) {
  if (!list.length) return
  const sorted = list.slice().sort((a, b) => compareSectorByLatestYi(a, b))
  const total = sorted.length
  sorted.forEach((row, idx) => {
    const pct = idx / total
    let colorIdx: number
    if (pct < 0.2) colorIdx = 0
    else if (pct < 0.4) colorIdx = 1
    else if (pct < 0.6) colorIdx = 2
    else if (pct < 0.8) colorIdx = 3
    else colorIdx = 4
    row.color = RANK_COLORS[colorIdx]
  })
}

// 午休偏移：13:01 之后的时间戳整体前移 90 分钟（11:30 ~ 13:01 = 90 分钟）
// 这样下午数据紧接上午，x 轴不会出现空白段
const LUNCH_OFFSET_MS = 90 * 60 * 1000

function timeStrToTs(timeStr: string): number {
  const s = String(timeStr).padStart(4, '0')
  const h = parseInt(s.slice(0, s.length - 2))
  const m = parseInt(s.slice(-2))
  const totalMin = h * 60 + m
  const d = new Date()
  let ts = new Date(d.getFullYear(), d.getMonth(), d.getDate(), h, m, 0).getTime()
  // 13:01 = 781 分钟，之后的时间前移 91 分钟
  if (totalMin >= 781) ts -= LUNCH_OFFSET_MS
  return ts
}

// 反向：虚拟时间戳 -> 显示标签
function tsToLabel(ts: number): string {
  const d = new Date()
  const morningEnd = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 11, 30, 0).getTime()
  // 如果 ts > 上午结束，说明是下午数据（已前移），还原真实时间
  const realTs = ts > morningEnd ? ts + LUNCH_OFFSET_MS : ts
  const rd = new Date(realTs)
  return `${rd.getHours()}:${String(rd.getMinutes()).padStart(2, '0')}`
}

function toYi(raw: string | number): number {
  const n = Number(raw)
  if (!n || isNaN(n)) return 0
  return n / 1e8
}

function calcNetFlow(mf: any): number {
  const si = toYi(mf?.superin?.[0]?.amount)
  const bi = toYi(mf?.bigin?.[0]?.amount)
  const so = toYi(mf?.superout?.[0]?.amount)
  const bo = toYi(mf?.bigout?.[0]?.amount)
  return (si + bi) - (so + bo)
}

// v 为亿单位；按原始金额区间格式化：[0,1万) 直接显示，[1万,1亿) 万，[1亿+) 亿
function formatFlowValue(v: number): string {
  if (isNaN(v)) return ''
  const sign = v < 0 ? '-' : ''
  const abs = Math.abs(v)
  if (abs < 1e-4) return sign + (abs * 1e8).toFixed(2)
  if (abs < 1) return sign + (abs * 1e4).toFixed(2) + '万'
  return sign + abs.toFixed(2) + '亿'
}

// 调试：本地存储 9:30~9:40 的 RT_MF 原始推送
const RT_MF_DEBUG_KEY = 'capital_flow_rt_mf_debug_930_940'

function snapshotSectorListForDebug(sectorList: SectorRow[]) {
  return sectorList.map((s) => ({
    key: s.key,
    name: s.name,
    symbol: s.symbol,
    market: s.market,
    latestYi: s.latestYi,
    series: s.series.slice(),
    displaySeries: s.series.slice()
  }))
}

function saveRtMfDebug(body: any, inst: string, sectorList: SectorRow[]) {
  const isRtMfDebugTime = (timeStr: string) => {
    const t = parseInt(String(timeStr).padStart(4, '0'), 10)
    return t >= 930 && t <= 940
  }
  const items = (body?.data || []).filter((item: any) => isRtMfDebugTime(item.time))
  if (!items.length) return
  try {
    const raw = localStorage.getItem(RT_MF_DEBUG_KEY)
    const store: Record<string, any[]> = raw ? JSON.parse(raw) : {}
    const code = inst || body.key?.code || 'unknown'
    const sectorSnapshot = snapshotSectorListForDebug(sectorList)
    items.forEach((item: any) => {
      const timeKey = String(item.time).padStart(4, '0')
      if (!store[timeKey]) store[timeKey] = []
      store[timeKey].push({
        receivedAt: new Date().toISOString(),
        inst: code,
        key: body.key,
        data: item,
        sectorList: sectorSnapshot
      })
    })
    localStorage.setItem(RT_MF_DEBUG_KEY, JSON.stringify(store))
  } catch {
    /* ignore */
  }
}

function todayRange() {
  const d = new Date()
  const y = d.getFullYear(), mo = d.getMonth(), day = d.getDate()
  return {
    min: new Date(y, mo, day, 9, 30, 0).getTime(),
    max: new Date(y, mo, day, 15, 0, 0).getTime()
  }
}

// 点击坐标到线段的最短像素距离
function pointToSegmentDist(px: number, py: number, x1: number, y1: number, x2: number, y2: number): number {
  const dx = x2 - x1
  const dy = y2 - y1
  const lenSq = dx * dx + dy * dy
  if (!lenSq) return Math.hypot(px - x1, py - y1)
  let t = ((px - x1) * dx + (py - y1) * dy) / lenSq
  t = Math.max(0, Math.min(1, t))
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy))
}

const LINE_HIT_RADIUS = 12
const LABEL_LINE_H = 20
const LABEL_OFFSET_X = 4
const GRID_TOP = 16
const GRID_BOTTOM = 32
const GRID_RIGHT = 130

// 右侧标签防重叠：按金额从高到低排列，相邻标签最多重叠 1/3 高度
function adjustLineLabelLayout(items: LineLabelItem[], chartHeight: number) {
  if (items.length <= 1) return
  const minStep = (LABEL_LINE_H * 2) / 3
  const minTop = GRID_TOP
  const maxTop = Math.max(minTop, chartHeight - GRID_BOTTOM - LABEL_LINE_H)

  items.sort((a, b) => {
    const av = parseLatestYi(a.row)
    const bv = parseLatestYi(b.row)
    if (av != null && bv != null && av !== bv) return bv - av
    return a.top - b.top
  })

  let prevTop = -Infinity
  items.forEach((item, idx) => {
    let top = item.top
    if (prevTop !== -Infinity) {
      top = Math.max(top, prevTop + minStep)
    }
    top = Math.min(Math.max(top, minTop), maxTop)
    item.top = top
    item.centerY = top + LABEL_LINE_H / 2
    item.zIndex = idx + 1
    prevTop = top
  })
}

interface LineLabelItem {
  key: string
  row: SectorRow
  left: number
  top: number
  centerY: number
  color: string
  name: string
  value: string
  zIndex: number
}

export default defineComponent({
  name: 'SentimentCapitalFlow',
  setup() {
    const route = useRoute()
    const chartRef = ref<HTMLElement | null>(null)
    const chartAreaRef = ref<HTMLElement | null>(null)
    let chart: echarts.ECharts | null = null
    let chartHasOption = false
    let isRenderingChart = false
    let renderRafId = 0
    let pendingFullRender = false
    let lastChartPointer: { x: number; y: number } | null = null
    let chartPointerInside = false
    // 首次加载有数据后，仅对排名第一的板块自动触发一次命令
    let firstAutoTriggered = false

    const state = reactive({
      token: (route.query.token as string) || '',
      isDark: computed(() => (route.query.theme as string) !== 'light'),
      isXp: computed(() => route.query.os === 'xp'),
      sectorList: [] as SectorRow[],
      loading: false
    })

    // 已选中（可见）的板块 key 列表，默认全选
    const selectedKeys = ref<string[]>([])
    const dropdownOpen = ref(false)

    // 是否存在当天数据：任一板块已落定点或有当前缓存分钟即视为有数据。
    // 切换交易日清盘后订阅无当天数据返回时，sectorList 全部为空 -> 展示空态。
    const hasData = computed(() => {
      console.log('state.sectorList', state.sectorList)
      return state.sectorList.some((s) => s.series.length > 1)
    })

    const filterLabel = computed(() => {
      const hidden = state.sectorList.filter((s) => !selectedKeys.value.includes(s.key))
      if (hidden.length === 0) return '请选择'
      return `已隐藏 ${hidden.map((s) => s.name).join('、')}`
    })

    // 点击外部关闭下拉
    const onClickOutside = (e: MouseEvent) => {
      const el = document.querySelector('.custom-select')
      if (el && !el.contains(e.target as Node)) dropdownOpen.value = false
    }

    const onFilterChange = () => {
      scheduleRenderChart(true)
    }

    const isTooltipVisible = () => {
      const tip = document.querySelector('.capital-flow-tooltip') as HTMLElement | null
      return !!tip && tip.offsetWidth > 0 && tip.offsetHeight > 0
    }

    const resetTooltipScroll = () => {
      requestAnimationFrame(() => {
        const tip = document.querySelector('.capital-flow-tooltip') as HTMLElement | null
        if (tip) tip.scrollTop = 0
      })
    }

    const scheduleRenderChart = (full = false) => {
      pendingFullRender = pendingFullRender || full
      if (renderRafId) return
      renderRafId = requestAnimationFrame(() => {
        renderRafId = 0
        const fullRender = pendingFullRender
        pendingFullRender = false
        renderChart(fullRender)
      })
    }

    const lineLabelItems = ref<LineLabelItem[]>([])
    // 曲线/文字悬停：只显示对应曲线和文字，其余全部隐藏
    const focusedKey = ref<string | null>(null)
    const labelHovering = ref(false)

    const visibleLineLabelItems = computed(() => {
      if (!focusedKey.value) return lineLabelItems.value
      const item = lineLabelItems.value.find((i) => i.key === focusedKey.value)
      return item ? [item] : []
    })

    const resolveSectorKeyAtPixel = (x: number, y: number): string | null => {
      if (!chart) return null
      const labelZoneLeft = chart.getWidth() - GRID_RIGHT - 20
      if (x >= labelZoneLeft) {
        let best: LineLabelItem | undefined
        let bestDist = Infinity
        for (const item of lineLabelItems.value) {
          const dist = Math.abs(y - item.centerY)
          if (dist < bestDist) {
            bestDist = dist
            best = item
          }
        }
        if (best && bestDist <= LABEL_LINE_H / 2 + 6) return best.key
        return null
      }

      const visible = getVisibleSortedSectors()
      let bestKey: string | null = null
      let bestDist = LINE_HIT_RADIUS + 1
      for (let i = visible.length - 1; i >= 0; i--) {
        const s = visible[i]
        const pts = s.series
        for (let j = 1; j < pts.length; j++) {
          const p0 = chart.convertToPixel('grid', pts[j - 1]) as number[]
          const p1 = chart.convertToPixel('grid', pts[j]) as number[]
          if (!p0 || !p1 || p0.some((v) => isNaN(v)) || p1.some((v) => isNaN(v))) continue
          const segMinX = Math.min(p0[0], p1[0]) - LINE_HIT_RADIUS
          const segMaxX = Math.max(p0[0], p1[0]) + LINE_HIT_RADIUS
          if (x < segMinX || x > segMaxX) continue
          const dist = pointToSegmentDist(x, y, p0[0], p0[1], p1[0], p1[1])
          if (dist < bestDist) {
            bestDist = dist
            bestKey = s.key
          }
        }
      }
      return bestDist <= LINE_HIT_RADIUS ? bestKey : null
    }

    // 跳转到对应板块的 K 线
    let lastOpenKey = ''
    let lastOpenAt = 0
    const openKline = (row: SectorRow) => {
      const now = Date.now()
      if (row.key === lastOpenKey && now - lastOpenAt < 400) return
      lastOpenKey = row.key
      lastOpenAt = now
      nextTick(() => {
        window.open(
          `gw://funccall?funcname=OnFireStyleEvent;Command=switchkline;param=<params><code>${row.symbol}</code><market_letter>${row.market.toUpperCase()}</market_letter></params>`
        )
      })
    }

    const getVisibleSortedSectors = () =>
      state.sectorList
        .filter((s) => s.series.length > 0 && selectedKeys.value.includes(s.key))
        .slice()
        .sort((a, b) => compareSectorByLatestYi(a, b, false))

    const syncFocus = () => {
      if (!chart) return
      const key = focusedKey.value
      const sectors = getVisibleSortedSectors()
      chart.setOption({
        series: sectors.map((s, idx) => ({
          id: s.key,
          z: key && s.key === key ? sectors.length + 10 : idx + 1,
          showSymbol: false,
          symbol: 'none',
          lineStyle: {
            width: key && s.key === key ? 3 : 1.5,
            opacity: key && s.key !== key ? 0 : 1
          },
          emphasis: {
            scale: false,
            focus: 'none',
            lineStyle: { width: 3 }
          }
        }))
      })
      chart.dispatchAction({ type: 'downplay' })
      if (key) {
        chart.dispatchAction({ type: 'highlight', seriesId: key })
      }
    }

    const restoreAxisTooltip = (x: number, y: number) => {
      if (!chart) return
      chart.dispatchAction({
        type: 'updateAxisPointer',
        currTrigger: 'mousemove',
        x,
        y
      })
      chart.dispatchAction({ type: 'showTip', x, y })
      resetTooltipScroll()
    }

    const clearFocus = () => {
      labelHovering.value = false
      chartPointerInside = false
      lastChartPointer = null
      const hadFocus = focusedKey.value !== null
      focusedKey.value = null
      if (hadFocus) syncFocus()
      chart?.dispatchAction({ type: 'hideTip' })
      chart?.dispatchAction({ type: 'updateAxisPointer', currTrigger: 'leave' })
    }

    const setFocus = (key: string | null) => {
      if (key === null) {
        clearFocus()
        return
      }
      if (focusedKey.value === key) return
      focusedKey.value = key
      syncFocus()
    }

    const onLabelEnter = (key: string) => {
      labelHovering.value = true
      setFocus(key)
      if (lastChartPointer) restoreAxisTooltip(lastChartPointer.x, lastChartPointer.y)
    }

    const handleChartAreaLeave = () => {
      clearFocus()
    }

    const handleLabelMouseLeave = (e: MouseEvent) => {
      const next = e.relatedTarget as Node | null
      labelHovering.value = false
      if (next && chartAreaRef.value?.contains(next)) return
      clearFocus()
    }

    const updateLineLabels = () => {
      if (!chart) {
        lineLabelItems.value = []
        return
      }
      const items: LineLabelItem[] = []
      getVisibleSortedSectors().forEach((s, idx) => {
        if (!s.series.length) return
        const px = chart!.convertToPixel('grid', s.series[s.series.length - 1]) as number[]
        if (!px || px.some((v) => isNaN(v))) return
        const v = s.series[s.series.length - 1][1]
        items.push({
          key: s.key,
          row: s,
          left: px[0] + LABEL_OFFSET_X,
          top: px[1] - LABEL_LINE_H / 2,
          centerY: px[1],
          color: s.color,
          name: s.name,
          value: isNaN(v) ? '' : formatFlowValue(v),
          zIndex: idx + 1
        })
      })
      adjustLineLabelLayout(items, chart.getHeight())
      lineLabelItems.value = items
    }

    // 图表点击：右侧按曲线末端 y 命中 label，左侧命中曲线
    const handleChartPixelClick = (e: any) => {
      if (!chart) return
      const x = e.offsetX ?? e.zrX ?? e.event?.offsetX
      const y = e.offsetY ?? e.zrY ?? e.event?.offsetY
      if (x == null || y == null) return

      if (focusedKey.value) {
        const row = state.sectorList.find((s) => s.key === focusedKey.value)
        if (row) {
          openKline(row)
          return
        }
      }

      const key = resolveSectorKeyAtPixel(x, y)
      if (key) {
        const row = state.sectorList.find((s) => s.key === key)
        if (row) openKline(row)
      }
    }

    const handleChartPixelMove = (e: any) => {
      const x = e.offsetX ?? e.zrX ?? e.event?.offsetX
      const y = e.offsetY ?? e.zrY ?? e.event?.offsetY
      if (x == null || y == null) {
        clearFocus()
        return
      }
      chartPointerInside = true
      lastChartPointer = { x, y }
      if (labelHovering.value) return
      setFocus(resolveSectorKeyAtPixel(x, y))
      restoreAxisTooltip(x, y)
    }

    const handleChartGlobalOut = () => {
      if (isRenderingChart) return
      clearFocus()
    }

    const buildChartSeries = () => {
      const focusKey = focusedKey.value
      const sectors = getVisibleSortedSectors()
      return sectors.map((s, idx) => ({
        id: s.key,
        name: s.name,
        type: 'line',
        z: focusKey && s.key === focusKey ? sectors.length + 10 : idx + 1,
        triggerLineEvent: true,
        showSymbol: false,
        smooth: false,
        lineStyle: {
          width: focusKey && s.key === focusKey ? 3 : 1.5,
          color: s.color,
          opacity: focusKey && s.key !== focusKey ? 0 : 1
        },
        itemStyle: { color: s.color },
        data: s.series,
        symbol: 'none',
        emphasis: {
          scale: false,
          focus: 'none',
          lineStyle: { width: 3, color: s.color }
        },
        cursor: 'pointer'
      }))
    }

    const calcAxisMax = () => {
      let maxTs = 0
      state.sectorList.forEach((s) => {
        if (s.series.length) {
          const last = s.series[s.series.length - 1][0]
          if (last > maxTs) maxTs = last
        }
      })
      if (!maxTs) {
        const d = new Date()
        maxTs = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 9, 30, 0).getTime()
      }
      return maxTs
    }

    const renderChart = (full = false) => {
      if (!chartRef.value) return
      if (!chart) {
        chart = echarts.init(chartRef.value)
        chart.getZr().on('click', handleChartPixelClick)
        chart.getZr().on('mousemove', handleChartPixelMove)
        chart.getZr().on('globalout', handleChartGlobalOut)
      }
      const keepTooltip = chartPointerInside || isTooltipVisible()
      const pointer = lastChartPointer ? { ...lastChartPointer } : null
      isRenderingChart = true
      const { min } = todayRange()
      const max = calcAxisMax()
      const series = buildChartSeries()
      if (full || !chartHasOption) {
        chart.setOption({
          backgroundColor: 'transparent',
          animation: false,
          grid: { left: 52, right: GRID_RIGHT, top: GRID_TOP, bottom: GRID_BOTTOM },
          tooltip: {
            trigger: 'axis',
            backgroundColor: '#fff',
            borderColor: '#e0e0e0',
            borderWidth: 1,
            confine: true,
            enterable: true,
            position(point: number[]) {
              return [point[0]+5, point[1]+5]
            },
            padding: [10, 14],
            textStyle: { color: '#333', fontSize: 14 },
            extraCssText: 'max-height:204px;overflow-y:auto;scrollbar-width:thin;scrollbar-color:#d9d9d9 transparent;',
            className: 'capital-flow-tooltip',
            axisPointer: {
              type: 'line',
              snap: true,
              label: { show: false }
            },
            formatter(params: any[]) {
              if (!params?.length) return ''
              const t = params[0].axisValue
              let html = `<div style="font-size:18px;color:#222;margin-bottom:4px">${tsToLabel(Number(t))}</div>`
              params
                .sort((a, b) => Number(b.value?.[1]) - Number(a.value?.[1]))
                .forEach((p) => {
                  const v = Number(p.value?.[1])
                  if (isNaN(v)) return
                  const valStr = formatFlowValue(v)
                  html += `<div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;font-size:14px;color:#333">` +
                    `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${p.color};flex-shrink:0"></span>` +
                    `<span style="color:#808080">${p.seriesName}：</span><span style="color:#585d8b">${valStr}</span>` +
                    `</div>`
                })
              return html
            }
          },
          xAxis: {
            type: 'time',
            min,
            max,
            axisPointer: {
              show: true,
              type: 'line',
              snap: true,
              label: { show: false }
            },
            axisLabel: { color: '#888', fontSize: 10, formatter: (v: number) => tsToLabel(v) },
            splitLine: { show: false },
            axisLine: { lineStyle: { color: '#444' } }
          },
          yAxis: {
            type: 'value',
            axisLabel: { color: '#888', fontSize: 10, formatter: (v: number) => `${v}亿` },
            splitLine: { lineStyle: { color: '#333', type: 'dashed' } }
          },
          series
        }, { replaceMerge: ['series'] })
        chartHasOption = true
      } else {
        chart.setOption({ xAxis: { max }, series })
      }
      isRenderingChart = false
      updateLineLabels()
      syncFocus()
      if (keepTooltip && pointer) {
        restoreAxisTooltip(pointer.x, pointer.y)
      }
    }
    // 处理 502 推送数据
    const onRtMf = (data: any) => {
      // console.log('处理 502 推送数据',data)
      const body = data?.QuoteData
      if (!body?.data?.length) return
      const inst = data.Inst as string
      const row = state.sectorList.find((s) => s.symbol === inst || s.key === inst)
      if (!row) return

      // 判断是否为全量历史推送（首个点时间早于已有数据），重置并重建
      const firstTs = timeStrToTs(body.data[0].time)
      if (row.series.length > 0 && firstTs <= row.series[0][0]) {
        row.series = []
      }

      body.data.forEach((item: any) => {
        const ts = timeStrToTs(item.time)
        const net = calcNetFlow(item.mf)
        const lastPt = row.series[row.series.length - 1]

        if (!lastPt) {
          row.series.push([ts, net])
        } else if (ts === lastPt[0]) {
          // 同一分钟多次推送：替换当前分钟累计值，实时更新
          const prevCumulative = row.series.length > 1 ? row.series[row.series.length - 2][1] : 0
          lastPt[1] = prevCumulative + net
        } else if (ts > lastPt[0]) {
          // 新分钟：追加累计点
          row.series.push([ts, lastPt[1] + net])
        }
      })

      const hasData = row.series.length > 0
      row.latestYi = hasData ? row.series[row.series.length - 1][1].toFixed(2) : '--'
      assignRankColors(state.sectorList)
      scheduleRenderChart(false)
      // 首次加载：全部板块均有 latestYi 后，取净流入（latestYi）最高者自动触发一次命令
      if (!firstAutoTriggered && state.sectorList.length > 0) {
        const allReady = state.sectorList.every((s) => s.latestYi !== '--')
        if (allReady) {
          const top = pickTopSectorByLatestYi(state.sectorList)
          if (top) {
            console.log('top',top)
            firstAutoTriggered = true
            openKline(top)
          }
        }
      }
      saveRtMfDebug(body, inst, state.sectorList)
    }

    const subscribeRtMf = (list: SectorRow[]) => {
      const wsInst = ws.websocket as any
      list.forEach((item) => {
        const marketId = MARKET_ID_MAP[item.market] ?? 12
        const key = `RT_MF_${marketId}_${item.symbol}`.toUpperCase()
        const message = [
          wsInst.convertMessage(
            wsInst.reqID++,
            MESSAGEID_DEF.MSGID_RT_MF_REG,
            {
              key: { oldmarketcode: item.market.toUpperCase(), code: item.symbol },
              pushflag: true,
              timestamp: true
            }
          )
        ]
        // console.log('RT_MF',key,message)
        wsInst.sendMessage(key, 'RT_MF', message)
      })
    }

    const fetchSectors = async () => {
      state.loading = true
      try {
        const res: any = await sentimentApi.getSectorList({ token: state.token })
        if (res?.code === 1 && res.data) {
          const raw = Array.isArray(res.data) ? res.data : res.data.list || []
          state.sectorList = raw.map((item: any, idx: number) => {
            const symbol = String(item.symbol ?? '')
            const market = (item.market ?? '').toLowerCase()
            return {
              name: item.name ?? symbol,
              symbol,
              market,
              key: symbol,
              color: '#888',
              series: [] as [number, number][],
              latestYi: '--'
            }
          })
          // 默认全选所有板块
          selectedKeys.value = state.sectorList.map((s) => s.key)
          subscribeRtMf(state.sectorList)
        }
      } catch (e) {
        console.error(e)
      } finally {
        state.loading = false
      }
    }

    // 点击"更新数据"：立即重新拉取板块并重新订阅，等待异步数据返回后刷新图表
    const refresh = async () => {
      await fetchSectors()
      await nextTick()
      scheduleRenderChart(true)
    }

    const resize = () => {
      chart?.resize()
      nextTick(() => {
        updateLineLabels()
        if (lastChartPointer && (chartPointerInside || isTooltipVisible())) {
          restoreAxisTooltip(lastChartPointer.x, lastChartPointer.y)
        }
      })
    }

    onMounted(() => {
      ws.websocket.on('onRT_MF', onRtMf);
      fetchSectors();
      nextTick(() => scheduleRenderChart(true));
      window.addEventListener('resize', resize);
      document.addEventListener('click', onClickOutside);
      (window as any).on_tradingday_changed = on_tradingday_changed;
    })
    
    onUnmounted(() => {
      delete (window as any).on_tradingday_changed
    })

    onBeforeUnmount(() => {
      ws.websocket.off('onRT_MF', onRtMf)
      window.removeEventListener('resize', resize)
      document.removeEventListener('click', onClickOutside)
      if (chart) {
        chart.getZr().off('click', handleChartPixelClick)
        chart.getZr().off('mousemove', handleChartPixelMove)
        chart.getZr().off('globalout', handleChartGlobalOut)
        chart.dispose()
        chart = null
      }
    })

    return { ...toRefs(state), chartRef, chartAreaRef, visibleLineLabelItems, focusedKey, onLabelEnter, handleChartAreaLeave, handleLabelMouseLeave, openKline, selectedKeys, dropdownOpen, filterLabel, onFilterChange, hasData, refresh }
  }
})
